import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import ExtractedCharacters from "./ExtractedCharacters";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import MediaGenerationToolbar from "@/components/story/MediaGenerationToolbar";
import { useToast } from "@/hooks/use-toast"; // Add this import for toast notifications
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom"; // Add this import
import { logger } from "@/lib/logger";

// Add these formatter functions at the top of the file, before the StoryContent component
const formatText = (text: string) => {
  if (!text) return null;
  
  // Split the text by double asterisks for bold text
  const boldSegments = text.split(/(\*\*[^*]+\*\*)/g);
  
  return (
    <>
      {boldSegments.map((segment, i) => {
        // Check if this segment is surrounded by double asterisks (bold)
        if (segment.startsWith('**') && segment.endsWith('**')) {
          // Remove the asterisks and render as bold
          const boldContent = segment.slice(2, -2);
          return <strong key={i}>{formatItalicText(boldContent)}</strong>;
        } else {
          // Process remaining text for italic formatting
          return formatItalicText(segment, i);
        }
      })}
    </>
  );
};

// Helper function to handle italic text
const formatItalicText = (text: string, parentKey?: number) => {
  if (!text) return null;
  
  // Split the text by single asterisks for italic text
  const italicSegments = text.split(/(\*[^*]+\*)/g);
  
  if (italicSegments.length === 1) {
    return text; // No italic formatting needed
  }
  
  return (
    <>
      {italicSegments.map((segment, i) => {
        const key = parentKey !== undefined ? `${parentKey}-${i}` : i;
        
        // Check if this segment is surrounded by single asterisks (italic)
        if (segment.startsWith('*') && segment.endsWith('*')) {
          // Remove the asterisks and render as italic
          return <em key={key}>{segment.slice(1, -1)}</em>;
        } else {
          return <span key={key}>{segment}</span>;
        }
      })}
    </>
  );
};

interface StoryContentProps {
  title: string;
  content: Array<{
    id: string;
    content: string;
    user_input: string | null;
    image_url?: string | null;
    audio_url?: string | null;
  }>;
  storyId: string;
  showCharacterExtraction?: boolean;
  canExtractCharacter: boolean;
  onPageChange?: (page: number) => void;
  viewMode?: "paginated" | "entire";
  
  // Props for media toolbar
  isCreator?: boolean;
  onGenerateImage?: () => void;
  onGenerateAudio?: () => void;
  isGeneratingImage?: boolean;
  isGeneratingAudio?: boolean;
  userCredits?: number;
  requiredCredits?: number;
  storyImage?: string | null;
  audioUrl?: string | null;
  imageGenerationProgress?: number;
  numImagesToGenerate?: number;
  disableAudio?: boolean;
  t: (key: string) => string;

  // Props for rewrite functionality
  rewritesAvailable?: number;
  isRewriting?: boolean;
  onRewritten?: () => void;
  story?: any; // To access story data including rewrites_available
}

const StoryContent = ({ 
  title, 
  content, 
  storyId, 
  showCharacterExtraction = false, 
  canExtractCharacter,
  onPageChange,
  viewMode = "paginated",
  
  // Media toolbar props with defaults
  isCreator = false,
  onGenerateImage = () => {},
  onGenerateAudio = () => {},
  isGeneratingImage = false,
  isGeneratingAudio = false,
  userCredits = 0,
  requiredCredits = 10,
  storyImage = null,
  audioUrl = null,
  imageGenerationProgress = 0,
  numImagesToGenerate = 4,
  disableAudio = false,
  t,

  // New props with defaults
  rewritesAvailable = 0,
  isRewriting = false,
  onRewritten = () => {},
  story = {},
}: StoryContentProps) => {
  // Add QueryClient for better cache management
  const queryClient = useQueryClient();
  const { toast } = useToast(); // Add this to use toast notifications
  const navigate = useNavigate(); // Add this hook

  // Get current page from localStorage or default to 0
  const [currentPage, setCurrentPage] = useState<number>(() => {
    const saved = localStorage.getItem(`story-page-${storyId}`);
    return saved ? parseInt(saved, 10) : 0;
  });
  
  // Add a temporary state for slider dragging
  const [tempSliderValue, setTempSliderValue] = useState<number>(currentPage);

  // Save current page to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(`story-page-${storyId}`, currentPage.toString());
    setTempSliderValue(currentPage); // Keep temp value in sync
    if (onPageChange) {
      onPageChange(currentPage);
    }
  }, [currentPage, storyId, onPageChange]);

  const totalPages = content?.length || 0;
  const currentContent = content?.[currentPage] || null;

  const goToPage = (page: number) => {
    const targetPage = Math.max(0, Math.min(page, totalPages - 1));
    setCurrentPage(targetPage);
    // Smooth scroll to top of content
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers to display (first, last, and pages around current)
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      // If 7 or fewer pages, show all
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const pages = [];
    // Always include first page
    pages.push(0);
    
    // Calculate range around current page
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(currentPage + 2, totalPages - 2);
    
    // Adjust range to always show 5 pages when possible
    if (startPage > 1) {
      pages.push('ellipsis-start');
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    if (endPage < totalPages - 2) {
      pages.push('ellipsis-end');
    }
    
    // Always include last page
    pages.push(totalPages - 1);
    
    return pages;
  };

  const { data: userProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile } = await supabase
        .from('profiles')
        .select('ai_provider')
        .eq('id', user.id)
        .single();

      return profile;
    }
  });

  // New state for tracking rewrite in progress
  const [isRewritingState, setIsRewritingState] = useState(isRewriting);
  
  // Add state to track rewritesAvailable 
  const [storyRewritesAvailable, setStoryRewritesAvailable] = useState<number>(
    story?.rewrites_available || rewritesAvailable || 0
  );
  
  // Update state when props change
  useEffect(() => {
    if (story?.rewrites_available !== undefined) {
      setStoryRewritesAvailable(story.rewrites_available);
    } else if (rewritesAvailable !== undefined) {
      setStoryRewritesAvailable(rewritesAvailable);
    }
  }, [story?.rewrites_available, rewritesAvailable]);

  // Improved soft refresh handler for rewrite completion
  const handleRewritten = async (updatedRewritesAvailable?: number) => {
    logger.log("StoryContent: handleRewritten called", { updatedRewritesAvailable });
    setIsRewritingState(false);
    
    // Update local state with new rewrites count
    if (updatedRewritesAvailable !== undefined) {
      setStoryRewritesAvailable(updatedRewritesAvailable);
    }
    
    try {
      toast({
        title: t('storyRewrite.rewriteComplete'),
        description: t('storyRewrite.refreshingContent'),
      });
      
      // Invalidate all relevant queries
      await queryClient.invalidateQueries({ queryKey: ['story-content', storyId] });
      await queryClient.invalidateQueries({ queryKey: ['story', storyId] });
      
      // Fetch fresh data directly
      const { data: freshContent, error } = await supabase
        .from('story_content')
        .select('*')
        .eq('story_id', storyId)
        .order('created_at', { ascending: true });
        
      if (error) throw error;
      
      if (freshContent) {
        // Update the query client cache with fresh data
        queryClient.setQueryData(['story-content', storyId], freshContent);
        
        // Find the page where user input is null (the page that needs continuation)
        const newPageIndex = freshContent.findIndex(content => content.user_input === null);
        
        // Navigate to that page or the last page if no null input is found
        const targetPage = newPageIndex >= 0 ? newPageIndex : freshContent.length - 1;
        goToPage(targetPage);
        
        // Update UI with the new current content
        if (freshContent[targetPage]?.image_url) {
          // If we're updating the parent component, use the callback
          if (onPageChange) {
            onPageChange(targetPage);
          }
        }
      }
    } catch (error) {
      logger.error("Error refreshing content:", error);
      toast({
        variant: "destructive",
        title: t('storyRewrite.refreshError'),
        description: t('storyRewrite.refreshErrorMessage'),
        action: (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => window.location.reload()}
          >
            {t('refresh')}
          </Button>
        ),
      });
    }
  };

  // Add a function to handle navigation to setup page
  const handleNavigateToSetup = () => {
    navigate(`/story/new/${storyId}`);
  };

  return (
    <div className="bg-white rounded-lg shadow-xl p-4 print:shadow-none print:border-none">
      <h1 className="text-muted-foreground text-3xl font-bold mb-8 text-center">{title}</h1>
      
      {/* Add media toolbar and display - only in paginated view mode */}
      {viewMode === "paginated" && isCreator && (
        <div className="mb-8">
          {/* Move toolbar outside of absolute positioning */}
          <div className="flex justify-end mb-4">
            <MediaGenerationToolbar 
              onGenerateImage={onGenerateImage}
              onGenerateAudio={onGenerateAudio}
              isGeneratingImage={isGeneratingImage}
              isGeneratingAudio={isGeneratingAudio}
              userCredits={userCredits}
              requiredCredits={requiredCredits}
              hasImage={!!storyImage}
              hasAudio={!!audioUrl}
              disableAudio={disableAudio}
              t={t}
              // New props for rewrite functionality
              isLastPage={false} // Is this the last page?
              storyId={storyId}
              contentId={currentContent?.id}
              rewritesAvailable={storyRewritesAvailable}
              isRewriting={isRewritingState}
              onRewritten={handleRewritten}
              onNavigatePrev={() => goToPage(currentPage - 1)}
              // New props for handling first page rewrite
              isFirstPage={currentPage === 0 && totalPages === 1}
              onNavigateToSetup={handleNavigateToSetup}
            />
          </div>
        </div>
      )}
                
      {/* Remove padding as toolbar is no longer overlapping */}
      <div>
        {/* Display generated image if available */}
        {storyImage && (
          <div className="mb-6">
            <img 
              src={storyImage} 
              alt="Story illustration" 
              className="rounded-md mx-auto w-auto max-h-100 md:max-h-80 shadow-md"
            />
          </div>
        )}
        
        {/* Display audio player if available */}
        {audioUrl && (
          <div className="mb-4 mt-4 flex justify-center">
            <audio 
              controls 
              src={audioUrl} 
              className="w-full max-w-md rounded-md shadow-sm"
            >
              Your browser does not support the audio element.
            </audio>
          </div>
        )}
        
        {/* Image generation loading state */}
        {isGeneratingImage && (
          <div className="mb-6 flex flex-col items-center justify-center p-8 border border-dashed border-gray-300 rounded-md bg-gray-50">
            <Loader2 className="h-10 w-10 animate-spin text-primary mb-2" />
            <p className="text-sm text-gray-500 mb-2">{t('imageGeneration.generatingImage')}</p>
            <div className="w-full max-w-xs bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-primary h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${(imageGenerationProgress / numImagesToGenerate) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {imageGenerationProgress} / {numImagesToGenerate}
            </p>
          </div>
        )}
        
        {/* Audio generation loading state */}
        {isGeneratingAudio && (
          <div className="mb-6 flex flex-col items-center justify-center p-8 border border-dashed border-gray-300 rounded-md bg-gray-50">
            <Loader2 className="h-10 w-10 animate-spin text-primary mb-2" />
            <p className="text-sm text-gray-500 mb-2">{t('audioGeneration.generatingAudio')}</p>
            <p className="text-xs text-gray-500">
              {t('audioGeneration.pleaseWait')}
            </p>
          </div>
        )}
      </div>
      
      <div className="prose prose-lg max-w-none mb-8 text-justify">
        {currentContent && (
          <div key={currentContent.id} className="mb-6 transition-opacity duration-300 ease-in-out">
            <p className="text-gray-800 leading-relaxed">{formatText(currentContent.content)}</p>
            {currentContent.user_input && (
              <p className="text-primary font-medium mt-2 italic">
                {t('storyEditor.choices.yourChoice')}: {formatText(currentContent.user_input)}
              </p>
            )}
          </div>
        )}
      </div>
      
      {/* Slider-based Pagination Controls with delayed page change */}
      {totalPages > 1 && (
        <div className="mt-8 flex flex-col items-center space-y-4">
          <div className="text-sm text-gray-500">
          {t('storyEditor.navigation.page')} {currentPage + 1} {t('storyEditor.navigation.of')} {totalPages}
          </div>
          
          <div className="w-full max-w-md flex items-center gap-2 px-2">
            <Button 
              variant="outline"
              size="icon"
              disabled={currentPage === 0} 
              onClick={() => goToPage(currentPage - 1)}
              className="shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="sr-only">Previous page</span>
            </Button>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild className="w-full mx-2">
                  <Slider
                    value={[tempSliderValue]}
                    min={0}
                    max={totalPages - 1}
                    step={1}
                    onValueChange={(values) => {
                      // Just update the temporary value during dragging
                      setTempSliderValue(values[0]);
                    }}
                    onValueCommit={(values) => {
                      // Only update the actual page after slider is released
                      goToPage(values[0]);
                    }}
                    className="w-full"
                  />
                </TooltipTrigger>
                <TooltipContent>
                  {/* Show the preview page during dragging */}
                  {t('storyEditor.navigation.page')} {tempSliderValue + 1} {t('storyEditor.navigation.of')} {totalPages}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <Button 
              variant="outline"
              size="icon"
              disabled={currentPage === totalPages - 1} 
              onClick={() => goToPage(currentPage + 1)}
              className="shrink-0"
            >
              <ChevronRight className="h-4 w-4" />
              <span className="sr-only">Next page</span>
            </Button>
          </div>
        </div>
      )}

      {showCharacterExtraction && userProfile?.ai_provider && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <ExtractedCharacters 
            storyId={storyId} 
            aiProvider={userProfile.ai_provider} 
            canExtractCharacter={canExtractCharacter}
          />
        </div>
      )}
    </div>
  );
};

export default StoryContent;
